/**
 * ReaderShell - Wrapper component for reader modes
 * 
 * Phase 3-C: Now consumes shellSettings from Page Builder configuration.
 * Settings control: progress bar, mode toggle, immersive mode, keyboard/swipe nav, chapter nav.
 */

import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useReaderContext, READER_MODES } from './hooks/useReaderContext';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  ArrowLeft,
  Rows3,
  Columns3,
  LayoutGrid,
  Home,
  Eye,
  EyeOff,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

const MODE_CONFIG = {
  [READER_MODES.VERTICAL]: { icon: Rows3, label: 'Vertical', shortLabel: 'V' },
  [READER_MODES.HORIZONTAL]: { icon: Columns3, label: 'Horizontal', shortLabel: 'H' },
  [READER_MODES.PAGE]: { icon: LayoutGrid, label: 'Page', shortLabel: 'P' },
};

const ReaderShell = ({ children, isAdmin = false, shellSettings = {}, footerSettings = {} }) => {
  const { 
    mode, 
    setMode, 
    metadata, 
    contentType,
    contentId,
    progress,
    currentPage,
    totalPages,
    headerVisible,
    toggleHeader,
    setHeaderVisible,
    isMobile,
  } = useReaderContext();
  const navigate = useNavigate();

  // Merge defaults with Page Builder settings
  const settings = {
    showProgressBar: true,
    showModeToggle: true,
    immersiveByDefault: false,
    allowModeSwitching: true,
    enableKeyboardNav: true,
    enableSwipeNav: true,
    showChapterNav: true,
    ...shellSettings,
  };

  // Apply immersiveByDefault on mount
  useEffect(() => {
    if (settings.immersiveByDefault && setHeaderVisible) {
      setHeaderVisible(false);
    }
  }, [settings.immersiveByDefault]);

  const handleBack = () => {
    if (contentType === 'comic') {
      navigate(`/series/${contentId}`);
    } else if (contentType === 'novel') {
      navigate(`/novel/${contentId}`);
    } else {
      navigate(-1);
    }
  };

  const getProgressDisplay = () => {
    if (mode === READER_MODES.VERTICAL) {
      return `${progress.percent || 0}%`;
    }
    const total = totalPages || 0;
    const current = currentPage + 1;
    return total > 0 ? `${current}/${total}` : '-';
  };

  const handlePrevChapter = () => {
    if (metadata?.prevUnit) {
      navigate(`/read/${contentType}/${contentId}/${metadata.prevUnit}?mode=${mode}`);
    }
  };

  const handleNextChapter = () => {
    if (metadata?.nextUnit) {
      navigate(`/read/${contentType}/${contentId}/${metadata.nextUnit}?mode=${mode}`);
    }
  };

  return (
    <div className="reader-shell min-h-screen bg-black flex flex-col" data-testid="reader-shell">
      {/* Header */}
      <header 
        className={`sticky top-0 z-50 bg-zinc-900/95 backdrop-blur border-b border-zinc-800 transition-transform duration-300 ${
          headerVisible ? 'translate-y-0' : '-translate-y-full'
        }`}
        data-testid="reader-header"
      >
        <div className="max-w-7xl mx-auto px-3 py-2 md:px-4 md:py-3">
          <div className="flex items-center justify-between gap-2">
            {/* Left: Back + Title */}
            <div className="flex items-center gap-2 md:gap-4 flex-1 min-w-0">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBack}
                data-testid="reader-back-button"
                className="text-zinc-400 hover:text-white flex-shrink-0"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              
              <div className="min-w-0 hidden sm:block">
                <h1 className="text-white font-medium text-sm md:text-base truncate">
                  {metadata?.title || (contentType === 'comic' ? 'Episode' : 'Chapter')}
                </h1>
                <p className="text-zinc-500 text-xs truncate">
                  {metadata?.seriesTitle || metadata?.novelTitle || ''}
                </p>
              </div>
            </div>

            {/* Center: Mode switcher (controlled by settings) */}
            {settings.showModeToggle && settings.allowModeSwitching && (
              <div className="flex items-center gap-0.5 bg-zinc-800 rounded-lg p-0.5" data-testid="reader-mode-toggle">
                {Object.entries(MODE_CONFIG).map(([m, config]) => {
                  const Icon = config.icon;
                  const isActive = mode === m;
                  return (
                    <Button
                      key={m}
                      variant={isActive ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setMode(m)}
                      data-testid={`reader-mode-${m}`}
                      className={`px-2 md:px-3 py-1.5 ${
                        isActive 
                          ? 'bg-orange-500 hover:bg-orange-600 text-white' 
                          : 'text-zinc-400 hover:text-white hover:bg-zinc-700'
                      }`}
                      title={config.label}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="hidden md:inline ml-1.5 text-xs">{config.label}</span>
                    </Button>
                  );
                })}
              </div>
            )}

            {/* Right: Progress + Actions */}
            <div className="flex items-center gap-2 flex-1 justify-end">
              {/* Chapter navigation */}
              {settings.showChapterNav && (
                <div className="flex items-center gap-1 hidden md:flex">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handlePrevChapter}
                    disabled={!metadata?.prevUnit}
                    data-testid="reader-prev-chapter"
                    className="text-zinc-400 hover:text-white disabled:opacity-30 h-8 w-8"
                    title="Previous chapter"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleNextChapter}
                    disabled={!metadata?.nextUnit}
                    data-testid="reader-next-chapter"
                    className="text-zinc-400 hover:text-white disabled:opacity-30 h-8 w-8"
                    title="Next chapter"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}

              {/* Progress badge */}
              {settings.showProgressBar && (
                <Badge 
                  variant="outline" 
                  data-testid="reader-progress-badge"
                  className="border-zinc-700 text-zinc-300 text-xs px-2 py-1 hidden sm:flex"
                >
                  {getProgressDisplay()}
                </Badge>
              )}

              {/* Toggle header visibility */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleHeader}
                data-testid="reader-toggle-header"
                className="text-zinc-400 hover:text-white hidden md:flex"
                title="Toggle header"
              >
                {headerVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>

              <Link to="/">
                <Button
                  variant="ghost"
                  size="icon"
                  data-testid="reader-home-button"
                  className="text-zinc-400 hover:text-white"
                >
                  <Home className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Progress bar under header */}
          {settings.showProgressBar && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-zinc-800" data-testid="reader-progress-bar">
              <div 
                className="h-full bg-orange-500 transition-all duration-300"
                style={{ 
                  width: mode === READER_MODES.VERTICAL 
                    ? `${progress.percent || 0}%`
                    : `${((currentPage + 1) / (totalPages || 1)) * 100}%`
                }}
              />
            </div>
          )}
        </div>
      </header>

      {/* Content area */}
      <main className="flex-1 min-h-0" data-testid="reader-content-area">
        {children}
      </main>

      {/* Mobile-only show header button when hidden */}
      {!headerVisible && isMobile && (
        <button
          onClick={toggleHeader}
          className="fixed top-2 right-2 z-40 bg-black/70 p-2 rounded-full"
          data-testid="reader-show-header-mobile"
          aria-label="Show header"
        >
          <Eye className="w-4 h-4 text-white" />
        </button>
      )}
    </div>
  );
};

export default ReaderShell;
